<script setup lang="ts">
import { computed, inject, ref, type Ref } from 'vue'
import { useFormControl } from '../../composables/useFormControl'
import { useBoundValue } from '../../composables/useBoundValue'
export type RadioValueType = string | number | boolean
export interface RadioChangeEvent {
  target: { value: RadioValueType | null | undefined; checked: boolean }
  nativeEvent: Event
  preventDefault: () => void
  stopPropagation: () => void
}
export interface RadioGroupContext {
  value: Ref<RadioValueType | null | undefined>
  disabled: Ref<boolean>
  name: Ref<string | undefined>
  onChange: (value: RadioValueType | null | undefined, event: RadioChangeEvent) => void
}
defineOptions({ inheritAttrs: false })
const props = withDefaults(defineProps<{
  checked?: boolean
  defaultChecked?: boolean
  value?: RadioValueType | null
  disabled?: boolean
  name?: string
}>(), { checked: undefined, defaultChecked: false, disabled: undefined })
const emit = defineEmits<{ 'update:checked': [checked: boolean]; change: [event: RadioChangeEvent] }>()
const { attrs, disabled, controlAttrs, onFieldChange, onFieldBlur } = useFormControl(props)
const group = inject<RadioGroupContext | null>('rrRadioGroupContext', null)
const inputRef = ref<HTMLInputElement | null>(null)
const innerChecked = ref(props.defaultChecked)
const mergedChecked = useBoundValue(props, 'checked', innerChecked)
const isChecked = computed(() => group ? group.value.value === props.value : Boolean(mergedChecked.value))
const isDisabled = computed(() => disabled.value || Boolean(group?.disabled.value))
const inputName = computed(() => props.name ?? group?.name.value)
function handleChange(event: Event) {
  if (isDisabled.value) return
  const checked = (event.target as HTMLInputElement).checked
  innerChecked.value = checked
  const changeEvent: RadioChangeEvent = { target: { value: props.value, checked }, nativeEvent: event, preventDefault: () => event.preventDefault(), stopPropagation: () => event.stopPropagation() }
  emit('update:checked', checked)
  emit('change', changeEvent)
  if (group) group.onChange(props.value, changeEvent)
  else return onFieldChange()
}
defineExpose({ focus: (options?: FocusOptions) => inputRef.value?.focus(options), blur: () => inputRef.value?.blur() })
</script>

<template>
  <label
    class="rr-radio-wrapper"
    :style="attrs.style as any"
    :class="[attrs.class, {
      'is-disabled': isDisabled,
      'is-checked': isChecked
    }]"
  >
    <span class="rr-radio">
      <input
        ref="inputRef"
        v-bind="{ ...controlAttrs(), ...(group ? { id: undefined } : {}) }"
        type="radio"
        :checked="isChecked"
        :disabled="isDisabled"
        :name="inputName"
        :value="value"
        class="rr-radio__input"
        @change="handleChange"
        @blur="onFieldBlur"
      />
      <span class="rr-radio__inner" />
    </span>
    <span v-if="$slots.default" class="rr-radio__label">
      <slot />
    </span>
  </label>
</template>

<style scoped>
.rr-radio-wrapper {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--rr-color-text);
  font-size: 13px;
  cursor: pointer;
  user-select: none;
  line-height: 1;
}

.rr-radio-wrapper.is-disabled {
  color: var(--rr-color-text-subtle);
  cursor: not-allowed;
}

.rr-radio {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.rr-radio__input {
  position: absolute;
  inset: 0;
  opacity: 0;
  margin: 0;
  cursor: pointer;
}

.rr-radio__inner {
  display: inline-block;
  width: 16px;
  height: 16px;
  box-sizing: border-box;
  border: 1px solid var(--rr-color-border-strong);
  border-radius: 50%;
  background: var(--rr-color-surface);
  transition: all 120ms ease;
}

.rr-radio-wrapper:hover:not(.is-disabled) .rr-radio__inner {
  border-color: var(--rr-color-primary);
}

.rr-radio-wrapper.is-checked .rr-radio__inner {
  border-color: var(--rr-color-primary);
}

.rr-radio-wrapper.is-checked .rr-radio__inner::after {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--rr-color-primary);
  transform: translate(-50%, -50%);
  content: "";
}

.rr-radio-wrapper.is-disabled .rr-radio__inner {
  background: var(--rr-color-surface-subtle);
  border-color: var(--rr-color-border);
}
</style>
